import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import type { Branch, DraftListItem, OpeningSession } from '../api/types'
import { Perm } from '../api/types'
import { draftStatusName, formatMoment, formatQty } from '../ui/format'
import { Banner, Button, Empty, Panel, Row, Spinner } from '@shop/ui'

/**
 * اللوحةُ الأساسيّة — <b>وما فيها ما يمنع عملَ اليوم إن كان ناقصًا</b>.
 *
 * لا رسومَ ولا مجاميعَ مبيعات: لا مبيعاتِ بعد. الذي يحتاجه الموظّفُ صباحًا ثلاثة:
 * ما تركه أمس ولم يُنهه، وهل فترةُ مستودعه مفتوحةٌ حتى تُقبل الكميّات، وأين
 * يبدأ. وكلُّ ما زاد يُخفي هذه الثلاثة.
 */
export default function Dashboard() {
  const { session, can } = useAuth()

  const drafts = useQuery({
    queryKey: ['drafts', true],
    queryFn: () => api<DraftListItem[]>('/api/v1/intake/drafts?openOnly=true&limit=100'),
    enabled: can(Perm.draftView),
  })

  const opening = useQuery({
    queryKey: ['opening'],
    queryFn: () => api<OpeningSession[]>('/api/v1/inventory/opening'),
    enabled: can(Perm.movementView),
  })

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => api<Branch[]>('/api/v1/branches'),
    enabled: can(Perm.branchView),
  })

  const waiting = (drafts.data ?? []).filter((d) => d.needsAttention)
  const openSessions = (opening.data ?? []).filter((s) => s.status === 'OPEN')
  const branchName = branches.data?.find((b) => b.id === session?.me.activeBranchId)?.nameAr

  return (
    <>
      <Panel title={`أهلًا ${session?.me.username ?? ''}`}>
        <Row label="الفرع الحاليّ">{branchName ?? '—'}</Row>
        <Row label="مسوّداتٌ مفتوحة">
          <span className="tabular">{drafts.data?.length ?? '—'}</span>
        </Row>
        <Row label="تنتظر نظرَك">
          <span className={`tabular ${waiting.length > 0 ? 'text-warn' : ''}`}>{waiting.length}</span>
        </Row>

        <div className="mt-4 flex flex-wrap gap-2">
          {can(Perm.draftCreate) && (
            <Link to="/intake">
              <Button tone="primary">ابدأ إدخالًا</Button>
            </Link>
          )}
          {can(Perm.draftView) && (
            <Link to="/drafts">
              <Button>مسوّداتي</Button>
            </Link>
          )}
        </div>
      </Panel>

      {can(Perm.movementView) && (
        <Panel title="فترةُ الرصيد الافتتاحيّ">
          {opening.isPending ? (
            <Spinner />
          ) : openSessions.length > 0 ? (
            <Banner tone="ok">
              مفتوحةٌ في {openSessions.length} مستودع — الاعتمادُ بكميّةٍ يعمل.
            </Banner>
          ) : (
            <Banner tone="warn">
              لا فترةَ مفتوحة. كلُّ اعتمادٍ بكميّةٍ سيُردّ حتى تُفتح واحدة — والاعتمادُ
              بلا كميّةٍ (كتالوجٌ فقط) يعمل.
            </Banner>
          )}
        </Panel>
      )}

      {can(Perm.draftView) && (
        <Panel title="آخرُ ما تركتَه مفتوحًا">
          {drafts.isPending && <Spinner />}
          {drafts.data?.length === 0 && <Empty>لا شيءَ مفتوح — كلُّ ما بدأتَه انتهى.</Empty>}

          <ul className="space-y-2">
            {(drafts.data ?? []).slice(0, 8).map((draft) => (
              <li key={draft.id}>
                <Link
                  to={`/drafts/${draft.id}`}
                  className={`block rounded-md border px-3 py-2 text-sm hover:bg-surface ${
                    draft.needsAttention ? 'border-warn/50 bg-warn/5' : 'border-line'
                  }`}
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span>{draft.nameAr ?? '(بلا اسم)'}</span>
                    <span className="tabular text-xs text-ink-soft" dir="ltr">
                      {draft.draftNo}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink-soft">
                    <span>{draftStatusName(draft.status)}</span>
                    <span className="tabular">كميّة: {formatQty(draft.qty)}</span>
                    <span className="tabular">{formatMoment(draft.createdAt)}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </>
  )
}
